import { EnvelopeSimple, FloppyDisk } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import useFileConversion from "@/lib/queries/useFileConversion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import ReferencesPane from "../ReferencesPane";
import { useTranslation } from "react-i18next";

const blockNoteStyle = `body { font-size: 16px; font-weight: normal;
  font-family: "Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Open Sans", "Segoe UI", "Roboto","Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; } .bn-block-content[data-content-type="bulletListItem"] { display: list-item; }`;

function OpenInOutlook({ currentContent }: { currentContent: string }) {
  const { t } = useTranslation("results");
  const parseContent = () => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(currentContent, "text/html");

      // Replace <br> tags with newlines
      doc.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));

      // Add newlines after block elements
      doc
        .querySelectorAll("p, div, h1, h2, h3, h4, h5, h6, li, tr, pre")
        .forEach((el) => {
          if (el.nextSibling) el.insertAdjacentText("afterend", "\n");
        });

      const plainText = doc.body.textContent || "";
      return plainText;
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      return "";
    }
  };
  return (
    <Button
      variant={"outline"}
      className="rounded-full shadow"
      onClick={() => {
        window.open(
          `https://outlook.office.com/mail/deeplink/compose?body=${encodeURIComponent(
            parseContent()
          )}`
        );
      }}
    >
      <EnvelopeSimple className="w-8 h-8 mr-2 md:w-6 md:h-6" />
      {t("actions.openInOutlook")}
    </Button>
  );
}

function SaveAs({
  currentContent,
  format,
  style,
}: {
  currentContent: string;
  format: string;
  style?: string;
}) {
  const { t } = useTranslation("results");
  const { mutate } = useFileConversion();
  const handleSaveAs = async (type: string) => {
    if (format === "html") {
      mutate({
        html: `<html><head>
      <style>
        ${blockNoteStyle}
        ${style || ""}
      </style>
      </head><body>${currentContent}</body></html>`,
        filename: `result.${type}`,
      });
    } else {
      console.warn("No registered save as option for ", format);
      return;
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={"outline"} className="rounded-full shadow">
          <FloppyDisk /> {t("actions.saveAs")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem onClick={() => handleSaveAs("pdf")}>
          {t("formats.pdf")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleSaveAs("html")}>
          {t("formats.html")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleSaveAs("txt")}>
          {t("formats.text")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleSaveAs("docx")}>
          {t("formats.word")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleSaveAs("odt")}>
          {t("formats.openDocument")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function CopyToClipboard({ currentContent }: { currentContent: string }) {
  const { t } = useTranslation("results");
  const toast = useToast();

  const handleCopy = async () => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(currentContent, "text/html");

      // Replace <br> tags with newlines
      doc.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));

      // Add newlines after block elements
      doc
        .querySelectorAll("p, div, h1, h2, h3, h4, h5, h6, li, tr, pre")
        .forEach((el) => {
          if (el.nextSibling) el.insertAdjacentText("afterend", "\n");
        });

      const plainText = doc.body.textContent || "";
      await navigator.clipboard.writeText(plainText);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }
    toast.toast({
      title: t("notifications.copySuccess.title"),
      description: t("notifications.copySuccess.description"),
    });
  };
  return (
    <Button
      variant={"outline"}
      className="rounded-full shadow"
      onClick={handleCopy}
    >
      {t("actions.copyToClipboard")}
    </Button>
  );
}

function ResultOptions({
  currentContent,
  format,
  references,
  style,
}: {
  currentContent: string;
  format: string;
  references?: string[] | null;
  style?: string;
}) {
  const { t } = useTranslation("results");

  switch (format) {
    case "html":
      return (
        <div className="fixed bottom-0 z-50 flex flex-row flex-wrap items-center justify-center max-w-full gap-2 py-6 ml-4 result-options-fixed">
          <OpenInOutlook currentContent={currentContent} />
          <SaveAs
            currentContent={currentContent}
            format={format}
            style={style}
          />
          <CopyToClipboard currentContent={currentContent} />
          <ReferencesPane references={references || null} />
        </div>
      );
    case "markdown":
      return (
        <div className="flex flex-row flex-wrap items-center max-w-full gap-2 mt-2">
          <OpenInOutlook currentContent={currentContent} />
          <SaveAs currentContent={currentContent} format={format} />
          <CopyToClipboard currentContent={currentContent} />
          <ReferencesPane references={references || null} />
        </div>
      );
    default:
      console.warn("No registered result pane for ", format);
      return (
        <div>
          {t("errors.noRegisteredPane")}
          <code>{JSON.stringify(currentContent, null, 2)}</code>
        </div>
      );
  }
}

export default ResultOptions;
